import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Layers, Package, GripVertical } from "lucide-react";
import { equivalencesApi, api } from "@/lib/api";
import { toast } from "sonner";

const PROD_VALUE = (id) => `product:${id}`;
const GROUP_VALUE = (id) => `group:${id}`;

/**
 * Gestion des "produits type" (matériaux par défaut) d'un type de commande.
 * Permet d'ajouter des produits spécifiques OU des groupes d'équivalence
 * qui apparaîtront pré-remplis sur chaque commande de ce type.
 */
export default function OrderTypeTemplatesDialog({ open, orderType, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add form state
  const [target, setTarget] = useState(""); // product:id or group:id
  const [defaultThickness, setDefaultThickness] = useState("");
  const [defaultQuantity, setDefaultQuantity] = useState("");
  const [allowEquiv, setAllowEquiv] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!orderType?.id) return;
    try {
      setLoading(true);
      const [t, p, g] = await Promise.all([
        equivalencesApi.listTemplates(orderType.id),
        api.getAllProducts(),
        equivalencesApi.listGroups(),
      ]);
      setTemplates(t.data || []);
      setProducts(p.data || []);
      setGroups(g.data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [orderType?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const options = useMemo(() => {
    const out = [];
    groups.forEach((g) => out.push({
      value: GROUP_VALUE(g.id),
      label: `[Groupe] ${g.name}`,
      unit: g.reference_unit,
    }));
    products.forEach((p) => out.push({
      value: PROD_VALUE(p.id),
      label: `${p.name} · ${p.supplier_name || "—"}`,
      unit: p.reference_unit,
    }));
    return out;
  }, [products, groups]);

  const handleAdd = async () => {
    if (!target) {
      toast.error("Choisis un produit ou un groupe");
      return;
    }
    const [kind, id] = target.split(":");
    const payload = {
      product_id: kind === "product" ? id : null,
      equivalence_group_id: kind === "group" ? id : null,
      default_thickness_mm: defaultThickness ? parseFloat(defaultThickness) : null,
      default_quantity: defaultQuantity ? parseFloat(defaultQuantity) : null,
      allow_equivalence: kind === "group" ? true : allowEquiv,
      position: templates.length,
    };
    try {
      setAdding(true);
      await equivalencesApi.createTemplate(orderType.id, payload);
      toast.success("Produit type ajouté");
      setTarget(""); setDefaultThickness(""); setDefaultQuantity(""); setAllowEquiv(true);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm("Retirer ce produit type ?")) return;
    try {
      await equivalencesApi.deleteTemplate(t.id);
      toast.success("Retiré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleEditField = async (t, field, value) => {
    try {
      const payload = { [field]: value };
      await equivalencesApi.updateTemplate(t.id, payload);
      // Optimistic local update
      setTemplates((ts) => ts.map((x) => x.id === t.id ? { ...x, [field]: value } : x));
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  // Drag & drop reorder
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch (_) { /* ignore drag init errors */ }
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id !== dragOverId) setDragOverId(id);
  };
  const handleDragEnd = () => { setDraggedId(null); setDragOverId(null); };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) { handleDragEnd(); return; }
    const ids = templates.map((t) => t.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) { handleDragEnd(); return; }
    const newOrder = [...templates];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    // Optimistic UI
    setTemplates(newOrder);
    handleDragEnd();
    try {
      await equivalencesApi.reorderTemplates(orderType.id, newOrder.map((t) => t.id));
      toast.success("Ordre mis à jour");
    } catch (err) {
      toast.error("Erreur de réordonnancement");
      await load();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="order-type-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-700" />
            Produits type — {orderType?.name}
          </DialogTitle>
          <DialogDescription>
            Liste de produits ou groupes d&apos;équivalence pré-remplis sur chaque commande
            de ce type. Les quantités seront à compléter dans la fiche complète de la
            commande, puis remonteront dans Stock & Prévisionnel.
          </DialogDescription>
        </DialogHeader>

        {/* Add form */}
        <div className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-6">
              <Label className="text-xs">Produit ou groupe</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="template-target-select">
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent className="max-h-[360px]">
                  {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label} · ({o.unit || "—"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Épaisseur défaut (mm)</Label>
              <Input
                type="number"
                step="0.5"
                value={defaultThickness}
                onChange={(e) => setDefaultThickness(e.target.value)}
                data-testid="template-thickness-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Quantité défaut</Label>
              <Input
                type="number"
                step="0.1"
                value={defaultQuantity}
                onChange={(e) => setDefaultQuantity(e.target.value)}
                data-testid="template-qty-input"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button onClick={handleAdd} disabled={adding || !target} className="w-full" data-testid="template-add-btn">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Ajouter
              </Button>
            </div>
          </div>
          {target.startsWith("product:") && (
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={allowEquiv} onCheckedChange={setAllowEquiv} data-testid="template-allow-equiv" />
              Autoriser les équivalences à la place de ce produit
            </label>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6">
              Aucun produit type configuré.
            </p>
          ) : (
            <ul className="divide-y border rounded-md" data-testid="templates-list">
              {templates.map((t) => (
                <li
                  key={t.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-move transition-colors ${
                    dragOverId === t.id && draggedId !== t.id ? "bg-blue-50 border-l-2 border-blue-500" : ""
                  } ${draggedId === t.id ? "opacity-40" : ""}`}
                  data-testid={`template-row-${t.id}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, t.id)}
                  onDragOver={(e) => handleDragOver(e, t.id)}
                  onDrop={(e) => handleDrop(e, t.id)}
                  onDragEnd={handleDragEnd}
                >
                  <GripVertical className="h-4 w-4 text-slate-400" />
                  <Package className="h-4 w-4 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {t.product_name || t.group_name || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                      {t.equivalence_group_id ? (
                        <Badge variant="secondary" className="text-[10px]">Groupe</Badge>
                      ) : (
                        <span>{t.supplier_name || "—"}</span>
                      )}
                      <span>·</span>
                      <span>{t.reference_unit || ""}</span>
                      {t.product_id && (
                        <label className="flex items-center gap-1 ml-2">
                          <Switch
                            checked={!!t.allow_equivalence}
                            onCheckedChange={(v) => handleEditField(t, "allow_equivalence", v)}
                            data-testid={`template-toggle-equiv-${t.id}`}
                          />
                          <span>équiv.</span>
                        </label>
                      )}
                    </div>
                  </div>
                  <Input
                    type="number"
                    step="0.5"
                    value={t.default_thickness_mm ?? ""}
                    onChange={(e) => setTemplates((ts) => ts.map((x) => x.id === t.id ? { ...x, default_thickness_mm: e.target.value === "" ? null : parseFloat(e.target.value) } : x))}
                    onBlur={(e) => handleEditField(t, "default_thickness_mm", e.target.value === "" ? null : parseFloat(e.target.value))}
                    className="h-8 w-20 text-xs"
                    placeholder="mm"
                    data-testid={`template-edit-thickness-${t.id}`}
                  />
                  <Input
                    type="number"
                    step="0.1"
                    value={t.default_quantity ?? ""}
                    onChange={(e) => setTemplates((ts) => ts.map((x) => x.id === t.id ? { ...x, default_quantity: e.target.value === "" ? null : parseFloat(e.target.value) } : x))}
                    onBlur={(e) => handleEditField(t, "default_quantity", e.target.value === "" ? null : parseFloat(e.target.value))}
                    className="h-8 w-24 text-xs"
                    placeholder="qté"
                    data-testid={`template-edit-qty-${t.id}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(t)}
                    data-testid={`template-delete-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
